import { Fragment, type ReactNode } from "react";
import { ActionIcon, Button, Menu, type MenuProps } from "@mantine/core";
import { IconChevronDown, IconDotsVertical } from "@tabler/icons-react";
import { Link } from "react-router-dom";

/** A clickable action or navigation item. */
export interface ActionMenuActionItem {
  /** Stable key for React. */
  key: string;
  label: ReactNode;
  leftSection?: ReactNode;
  /** Mantine color (e.g. "red" for destructive). */
  color?: string;
  disabled?: boolean;
  onClick?: () => void;
  /** When set, the item renders as a react-router `Link` to this path. */
  to?: string;
  /** Render a divider immediately before this item. */
  dividerBefore?: boolean;
  /** Native title attribute — e.g. explaining why a disabled item is unavailable. */
  title?: string;
}

/** A section heading inside the dropdown (renders a Menu.Label). */
export interface ActionMenuSection {
  key: string;
  sectionLabel: ReactNode;
  /** Render a divider immediately before this section heading. */
  dividerBefore?: boolean;
}

export type ActionMenuEntry = ActionMenuActionItem | ActionMenuSection;

export interface ActionMenuProps {
  /** Entries to show in the dropdown. Falsy entries are skipped. */
  items: (ActionMenuEntry | false | null | undefined)[];
  /**
   * Trigger button label. Omit for an icon-only kebab trigger (set `ariaLabel`).
   */
  label?: ReactNode;
  /** Left icon on a labelled button trigger. */
  leftSection?: ReactNode;
  /** Optional header line at the top of the dropdown (a Menu.Label). */
  header?: ReactNode;
  variant?: string;
  color?: string;
  size?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Required for the icon-only trigger (accessibility). */
  ariaLabel?: string;
  position?: MenuProps["position"];
  width?: MenuProps["width"];
}

function isSection(e: ActionMenuEntry): e is ActionMenuSection {
  return "sectionLabel" in e;
}

/**
 * Shared actions menu — a single Mantine `Menu` wrapper so every "kebab / ▾
 * actions" popover across the app looks and behaves the same (portal'd so it
 * isn't clipped, consistent position/shadow). Pass `label` for a button trigger
 * or omit it for an icon-only kebab. Items can be actions (`onClick`) or
 * navigation (`to`), grouped with `sectionLabel` entries and `dividerBefore`.
 */
export function ActionMenu({
  items,
  label,
  leftSection,
  header,
  variant = "light",
  color,
  size = "xs",
  loading,
  disabled,
  ariaLabel = "Actions",
  position = "bottom-end",
  width,
}: ActionMenuProps) {
  const entries = items.filter((i): i is ActionMenuEntry => Boolean(i));
  // Nothing to act on (only section labels) → render no trigger.
  if (!entries.some((e) => !isSection(e))) return null;

  const trigger =
    label !== undefined ? (
      <Button
        variant={variant}
        color={color}
        size={size}
        loading={loading}
        disabled={disabled}
        leftSection={leftSection}
        rightSection={<IconChevronDown size={14} />}
      >
        {label}
      </Button>
    ) : (
      <ActionIcon
        variant={variant === "light" ? "subtle" : variant}
        color={color}
        size={size === "xs" ? "md" : size}
        loading={loading}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <IconDotsVertical size={16} />
      </ActionIcon>
    );

  return (
    <Menu position={position} withinPortal shadow="md" width={width}>
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        {header && <Menu.Label>{header}</Menu.Label>}
        {entries.map((entry) => {
          if (isSection(entry)) {
            return (
              <Fragment key={entry.key}>
                {entry.dividerBefore && <Menu.Divider />}
                <Menu.Label>{entry.sectionLabel}</Menu.Label>
              </Fragment>
            );
          }
          // Two concrete branches so Mantine's polymorphic Menu.Item typing
          // resolves (a conditionally-spread `to` confuses the union).
          return (
            <Fragment key={entry.key}>
              {entry.dividerBefore && <Menu.Divider />}
              {entry.to ? (
                <Menu.Item
                  component={Link}
                  to={entry.to}
                  leftSection={entry.leftSection}
                  color={entry.color}
                  disabled={entry.disabled}
                  onClick={entry.onClick}
                  title={entry.title}
                >
                  {entry.label}
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={entry.leftSection}
                  color={entry.color}
                  disabled={entry.disabled}
                  onClick={entry.onClick}
                  title={entry.title}
                >
                  {entry.label}
                </Menu.Item>
              )}
            </Fragment>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
